/**
 * DECKSTR.GG — Arena Parser: Router (Browser Edition)
 * ────────────────────────────────────────────────────
 * Turns noisy MTGA Player.log text into structured JSON events.
 * Uses brace-balanced extraction to handle interleaved noise.
 * 
 * Ported from bot/src/arena/router.js — logic preserved,
 * converted to TypeScript ES module for browser use.
 */

// ─── Types ────────────────────────────────────────

export interface RouterEvent {
    kind: 'GRE_TO_CLIENT' | 'CLIENT_TO_GRE' | 'MATCH_STATE_CHANGE' | 'UNKNOWN_JSON';
    ts: number;
    matchId: string | null;
    direction: string | null;
    payload: any;
}

// ─── Router Class ─────────────────────────────────

export class Router {
    private maxBufferChars: number;
    private _textBuffer: string;

    constructor({ maxBufferChars = 2_000_000 } = {}) {
        this.maxBufferChars = maxBufferChars;
        this._textBuffer = '';
    }

    /**
     * Push raw text (stream chunk). Returns an array of routed events.
     */
    pushChunk(chunkText: string): RouterEvent[] {
        if (typeof chunkText !== 'string' || chunkText.length === 0) return [];

        this._textBuffer += chunkText;
        if (this._textBuffer.length > this.maxBufferChars) {
            this._textBuffer = this._textBuffer.slice(
                this._textBuffer.length - this.maxBufferChars
            );
        }

        const outEvents: RouterEvent[] = [];
        const { objects, remainder } = Router.extractJsonObjects(this._textBuffer);
        this._textBuffer = remainder;

        for (const obj of objects) {
            const ev = Router.classify(obj);
            if (!ev) continue;
            outEvents.push(ev);
        }

        return outEvents;
    }

    /**
     * Classify a parsed JSON object into an event type.
     */
    static classify(obj: any): RouterEvent | null {
        if (!obj || typeof obj !== 'object') return null;

        // GRE → Client: greToClientEvent wrapper
        if (obj.greToClientEvent) {
            return {
                kind: 'GRE_TO_CLIENT',
                ts: Router._coerceTs(obj.timestamp) ?? Date.now(),
                matchId: Router._inferMatchId(obj),
                direction: 'GRE→Client',
                payload: obj,
            };
        }

        // GRE → Client: top-level greToClientMessages array
        if (Array.isArray(obj.greToClientMessages)) {
            return {
                kind: 'GRE_TO_CLIENT',
                ts: Router._coerceTs(obj.timestamp) ?? Date.now(),
                matchId: Router._inferMatchId(obj),
                direction: 'GRE→Client',
                payload: {
                    ...obj,
                    greToClientEvent: { greToClientMessages: obj.greToClientMessages },
                },
            };
        }

        // GRE → Client: nested in payload
        if (Array.isArray(obj?.payload?.greToClientMessages)) {
            return {
                kind: 'GRE_TO_CLIENT',
                ts: Router._coerceTs(obj.timestamp) ?? Date.now(),
                matchId: Router._inferMatchId(obj),
                direction: 'GRE→Client',
                payload: {
                    ...obj.payload,
                    greToClientEvent: { greToClientMessages: obj.payload.greToClientMessages },
                },
            };
        }

        if (obj?.payload?.greToClientEvent?.greToClientMessages) {
            return {
                kind: 'GRE_TO_CLIENT',
                ts: Router._coerceTs(obj.timestamp) ?? Date.now(),
                matchId: Router._inferMatchId(obj),
                direction: 'GRE→Client',
                payload: obj.payload,
            };
        }

        // Client → GRE
        if (
            obj.clientToMatchServiceMessageType ||
            obj.payload?.type?.startsWith?.('ClientMessageType_')
        ) {
            return {
                kind: 'CLIENT_TO_GRE',
                ts: Router._coerceTs(obj.timestamp) ?? Date.now(),
                matchId: Router._inferMatchId(obj),
                direction: 'Client→GRE',
                payload: obj,
            };
        }

        // Match state changes (for auto-start/auto-stop)
        if (obj?.matchGameRoomStateChangedEvent || obj?.payload?.matchGameRoomStateChangedEvent) {
            return {
                kind: 'MATCH_STATE_CHANGE',
                ts: Router._coerceTs(obj.timestamp) ?? Date.now(),
                matchId: Router._inferMatchId(obj),
                direction: null,
                payload: obj?.payload || obj,
            };
        }

        // Unknown JSON
        return {
            kind: 'UNKNOWN_JSON',
            ts: Date.now(),
            matchId: Router._inferMatchId(obj),
            direction: null,
            payload: obj,
        };
    }

    // ─── Helpers ───────────────────────────────────

    static _coerceTs(ts: any): number | null {
        if (ts == null) return null;
        if (typeof ts === 'number') {
            if (ts > 1_000_000_000_000 && ts < 99_999_999_999_999) return ts;
            return null;
        }
        if (typeof ts === 'string') {
            if (/^\d{13}$/.test(ts)) return Number(ts);
            return null;
        }
        return null;
    }

    static _inferMatchId(obj: any): string | null {
        return obj.matchId || obj?.payload?.matchId || null;
    }

    /**
     * Brace-balanced JSON extraction from noisy text buffer.
     * Returns { objects: [...], remainder: "..." }
     *
     * Handles braces inside strings (basic escape handling).
     * This is the critical extraction algorithm that makes the parser
     * work with Arena's interleaved log format.
     */
    static extractJsonObjects(text: string): { objects: any[]; remainder: string } {
        const objects: any[] = [];
        let depth = 0;
        let start = -1;
        let inString = false;
        let escape = false;

        for (let i = 0; i < text.length; i++) {
            const ch = text[i];

            if (inString) {
                if (escape) {
                    escape = false;
                } else if (ch === '\\') {
                    escape = true;
                } else if (ch === '"') {
                    inString = false;
                }
                continue;
            } else {
                if (ch === '"') {
                    inString = true;
                    continue;
                }
            }

            if (ch === '{') {
                if (depth === 0) start = i;
                depth++;
            } else if (ch === '}') {
                if (depth > 0) depth--;
                if (depth === 0 && start !== -1) {
                    const chunk = text.slice(start, i + 1);
                    try {
                        objects.push(JSON.parse(chunk));
                    } catch {
                        // Not valid JSON; ignore
                    }
                    start = -1;
                }
            }
        }

        // Partial JSON at end — keep as remainder for next chunk
        let remainder = '';
        if (depth > 0 && start !== -1) remainder = text.slice(start);

        return { objects, remainder };
    }
}
